import { Modal, Drawer } from "@/components";
import LoginContentNew from "./loginContentNew";
import { commonStore } from "@/stores/quick-order";

import styles from "./index.module.scss";
import type { FormValue, FormErrors } from "@/components";
import { ConfigProvider } from "antd";
import staticRender from "./staticRender";
import { LoginInvalidException } from "@/const/constException";
import {
  LoginCustomProvider,
  type LoginCustomization,
} from "./customization";
import { deriveTokens, applyTokensToRoot, tokensToAntd } from "./tokens";
import { injectWidgetOverrides } from "./widget-overrides";
import { setCompleteContext } from "./complete/state";

export interface LoginProps extends LoginCustomization {
  closable?: boolean;
  onCancel?: () => void;
  intercept?: boolean;
  loginSuccess?: null | (() => void);
}

const DEFAULT_PRIMARY = "#1677ff"; // antd default — used when tenant doesn't pass `theme`

const login = (
  props: LoginProps = {}
): Promise<string | (FormValue | FormErrors)> | undefined => {
  const {
    closable = false,
    onCancel,
    intercept = true,
    loginSuccess,
    theme,
    text,
    className,
    antdToken,
  } = props;

  // Inject widget CSS once (functional defaults), then derive the tenant's
  // full palette from `theme.primary` + `theme.background` and apply the
  // resulting CSS vars on :root so they cascade to every widget-managed
  // surface (login modal, mobile drawer, Oops popup, antd primary buttons).
  injectWidgetOverrides();
  const tokens = deriveTokens(theme ?? { primary: DEFAULT_PRIMARY });
  applyTokensToRoot(tokens);
  const resolvedAntdToken = tokensToAntd(tokens, antdToken);

  const customization: LoginCustomization = { theme, text, className, antdToken };
  const rootClass = [styles.login_modal, "peblla-login-root", className]
    .filter(Boolean)
    .join(" ");

  // 把当前流程的主题/文案/类名塞给 complete 弹窗（loginContent 走完登录后会触发）
  setCompleteContext({ customization, rootClass, antdToken: resolvedAntdToken });
  const { isOpenLogin } = commonStore;

  const _login = (resolve: any, reject: any) => {
    if (isOpenLogin) return;
    commonStore.isOpenLogin = true;
    if (window.innerWidth > 768) {
      Modal.alert({
        title: text?.title ?? "Sign In",
        titleProps: { align: "left" },
        footer: null,
        maskClosable: false,
        className: rootClass,
        content: (modalProps) => (
          <ConfigProvider wave={{ disabled: true }} theme={{ token: resolvedAntdToken }}>
            <LoginCustomProvider value={customization}>
              <LoginContentNew
                loginSuccess={loginSuccess}
                onFinish={(loginRes: any) => {
                  modalProps?.onCancel?.();
                  const data = loginRes?.data ?? {};
                  resolve({ ok: true, member: data.member, sessionId: data.sessionId } as any);
                  onCancel?.();
                  commonStore.isOpenLogin = false;
                }}
                onFinishFailed={(error) => {
                  reject(error);
                  commonStore.isOpenLogin = false;
                }}
              />
            </LoginCustomProvider>
          </ConfigProvider>
        ),
        onCancel: () => {
          onCancel?.();
          commonStore.isOpenLogin = false;
        },
      });
    } else if (window.innerWidth <= 768) {
      if (isOpenLogin) return;
      staticRender({
        title: text?.title ?? "Sign In",
        open: commonStore.isOpenLogin || false,
        placement: "bottom",
        rootClassName: rootClass,
        zIndex: 1010,
        maskClosable: false,
        onClose: () => {
          onCancel?.();
          commonStore.isOpenLogin = false;
        },
        content: (modalProps) => {
          return (
            <ConfigProvider wave={{ disabled: true }} theme={{ token: resolvedAntdToken }}>
              <LoginCustomProvider value={customization}>
                <LoginContentNew
                  onCancelDrawer={() => {
                    onCancel?.();
                    // @ts-ignore
                    modalProps?.onClose();
                  }}
                  onFinish={(loginRes: any) => {
                    onCancel?.();
                    const data = loginRes?.data ?? {};
                    resolve({ ok: true, member: data.member, sessionId: data.sessionId } as any);
                    commonStore.isOpenLogin = false;
                  }}
                  onFinishFailed={(error) => {
                    reject(error);
                    commonStore.isOpenLogin = false;
                  }}
                />
              </LoginCustomProvider>
            </ConfigProvider>
          );
        },
      });
    }
  };

  return new Promise((resolve, reject) => {
    if (intercept) {
      Modal.info({
        title: LoginInvalidException?.title,
        titleStyle: { color: "var(--peblla-primary)" },
        className: "overall_situation_modal_tips",
        content: LoginInvalidException?.content,
        closable: false,
        maskClosable: false,
        footerProps: { ghost: false },
        onCancel: () => {
          _login(resolve, reject);
        },
      });
    } else {
      _login(resolve, reject);
    }
  });
};

export default login;
